/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import PageContentContainer from "../../Container/PageContentContainer";
import PageHeadline from "../../PageHeadline/PageHeadline";
import React, { useEffect, useRef, useState } from "react";
import ContentBox from "../../ContentBox/ContentBox";
import { Grid, Typography, Box, TextField } from "@mui/material";

import ButtonLoading from "../../Button/ButtonLoading";
import ApiClient from "../../../services/ApiClient";
import { enqueueSnackbar } from "notistack";

export default function ClearAndImportEConsult() {
  const [isLoading, setIsLoading] = useState(false);
  const [output, setOutput] = useState<string>("");
  const [caseId, setCaseId] = useState<string>("");

  const outputEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [output]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setOutput("");

    try {
      const parsedCaseId = Number.parseInt(caseId, 10);
      const payload: { caseId?: number } = {};

      if (!Number.isNaN(parsedCaseId) && parsedCaseId > 0) {
        payload.caseId = parsedCaseId;
      }

      const response = await ApiClient.post("/lb/services/command/clear_and_import_econsult", {
        body: JSON.stringify(payload),
      });

      const data = (response?.data ?? response) as { output?: string };
      setOutput(data?.output ?? "");
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Fehler beim ausführen des Commands", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContentContainer size={"tiny"}>
      <PageHeadline main="Ey, kannst du mal eben die eAkte entfernen und den Import starten" />

      <ContentBox>
        <Grid container sx={{ alignItems: "center" }} spacing={1}>
          <Grid size={12} sx={{ paddingInline: 1 }}>
            <TextField
              label="Case ID (optional)"
              type="number"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              disabled={isLoading}
              helperText="Leer lassen, um ohne Case-Reset zu importieren."
            />
          </Grid>
          <Grid size={12} sx={{ paddingInline: 3 }}>
            <ButtonLoading variant={"contained"} onClick={handleSubmit} isLoading={isLoading}>
              Import starten
            </ButtonLoading>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Output
          </Typography>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
              maxHeight: 360,
              overflow: "auto",
              backgroundColor: "background.default",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap", // keeps new lines; wraps long lines
              wordBreak: "break-word",
            }}
          >
            {output ? output : "—"}
            <div ref={outputEndRef} />
          </Box>
        </Grid>
      </ContentBox>
    </PageContentContainer>
  );
}
